import React, { useEffect } from 'react';

/**
 * usePageMeta — per-route SEO head manager (no react-helmet dependency).
 *
 * Emits, per-route:
 *   • <title>, <meta name="description">, <meta name="keywords">
 *   • <link rel="canonical">, <meta property="og:url">
 *   • Open Graph + Twitter Card title / description / image / type
 *   • <html lang> and <html dir>
 *   • 15 <link rel="alternate" hreflang="…"> tags + x-default (when hreflangPath given)
 *   • BreadcrumbList JSON-LD (when `breadcrumbs` array given)
 *
 * Restores the previous <title> + lang/dir on unmount so SPA navigation stays clean.
 * Cleans up its dynamic hreflang links and JSON-LD script on unmount.
 */
const HOST = 'https://zakat.international';

// 15-language set — kept in sync with translationsV2.js + sitemap.xml
const LANGS = [
  'en', 'ar', 'ur', 'hi', 'bn', 'ml', 'fa',
  'fr', 'tr', 'ms', 'id', 'ru', 'sw', 'uz', 'ps',
];

const setMeta = (selector, attr, value, createIfMissing = true) => {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(selector);
  if (!el && createIfMissing) {
    el = document.createElement(selector.startsWith('meta')
      ? 'meta'
      : selector.startsWith('link')
      ? 'link'
      : 'meta');
    if (selector.startsWith('meta[name=')) {
      const name = selector.match(/name="([^"]+)"/)?.[1];
      if (name) el.setAttribute('name', name);
    } else if (selector.startsWith('meta[property=')) {
      const prop = selector.match(/property="([^"]+)"/)?.[1];
      if (prop) el.setAttribute('property', prop);
    } else if (selector.startsWith('link[rel=')) {
      const rel = selector.match(/rel="([^"]+)"/)?.[1];
      if (rel) el.setAttribute('rel', rel);
    }
    document.head.appendChild(el);
  }
  if (el) el.setAttribute(attr, value);
};

export const usePageMeta = ({
  title,
  description,
  keywords,
  canonical,
  lang,
  dir,
  ogImage,
  ogType,
  // Path used for hreflang generation, e.g. '/inheritance'. When provided,
  // the hook stamps 15 <link rel="alternate" hreflang> tags + x-default
  // pointing at the canonical site host with `?lang=XX` deeplinks.
  hreflangPath,
  // Breadcrumb trail for BreadcrumbList JSON-LD (Google rich results).
  // Example: [{ name: 'Home', url: '/' }, { name: 'Will', url: '/will' }]
  breadcrumbs,
  // FAQPage JSON-LD entries. Each: { q: string, a: string }
  faqs,
  // HowTo JSON-LD steps. Each: { name: string, text: string }
  // Optional: pass { name: 'Overall howto name', steps: [...] } to override.
  howto,
} = {}) => {
  // Callers often pass FRESH arrays/objects each render (context inputs
  // change on every keystroke inside the calculator). Stringifying deps
  // once keeps the effect stable, avoiding a full head-tag rebuild per
  // keystroke. Guarded null/undefined so we don't force JSON.stringify
  // on absent props.
  const breadcrumbsKey = React.useMemo(
    () => (breadcrumbs ? JSON.stringify(breadcrumbs) : ''),
    [breadcrumbs],
  );
  const faqsKey = React.useMemo(
    () => (faqs ? JSON.stringify(faqs) : ''),
    [faqs],
  );
  const howtoKey = React.useMemo(
    () => (howto ? JSON.stringify(howto) : ''),
    [howto],
  );
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const crumbsData = breadcrumbsKey ? JSON.parse(breadcrumbsKey) : null;
    const faqsData = faqsKey ? JSON.parse(faqsKey) : null;
    const howtoData = howtoKey ? JSON.parse(howtoKey) : null;
    const prev = {
      title: document.title,
      lang: document.documentElement.getAttribute('lang'),
      dir:  document.documentElement.getAttribute('dir'),
    };

    if (title) document.title = title;
    if (lang)  document.documentElement.setAttribute('lang', lang);
    if (dir)   document.documentElement.setAttribute('dir',  dir);

    if (description) {
      setMeta('meta[name="description"]', 'content', description);
      setMeta('meta[property="og:description"]', 'content', description);
      setMeta('meta[name="twitter:description"]', 'content', description);
    }
    if (keywords) {
      setMeta('meta[name="keywords"]', 'content', keywords);
    }
    if (title) {
      setMeta('meta[property="og:title"]', 'content', title);
      setMeta('meta[name="twitter:title"]', 'content', title);
    }
    if (canonical) {
      setMeta('link[rel="canonical"]', 'href', canonical);
      setMeta('meta[property="og:url"]', 'content', canonical);
    }
    if (ogImage) {
      setMeta('meta[property="og:image"]', 'content', ogImage);
      setMeta('meta[name="twitter:image"]', 'content', ogImage);
    }
    if (ogType) {
      setMeta('meta[property="og:type"]', 'content', ogType);
    }

    // Per-page hreflang stamping ------------------------------------------------
    const alternateLinks = [];
    if (hreflangPath) {
      // Clear any previously-injected alternates before stamping fresh ones.
      document.head.querySelectorAll('link[data-dyn-hreflang="1"]').forEach((n) => n.remove());
      LANGS.forEach((L) => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', L);
        link.setAttribute('data-dyn-hreflang', '1');
        const sep = hreflangPath.includes('?') ? '&' : '?';
        link.setAttribute('href', `${HOST}${hreflangPath}${sep}lang=${L}`);
        document.head.appendChild(link);
        alternateLinks.push(link);
      });
      const xdef = document.createElement('link');
      xdef.setAttribute('rel', 'alternate');
      xdef.setAttribute('hreflang', 'x-default');
      xdef.setAttribute('data-dyn-hreflang', '1');
      xdef.setAttribute('href', `${HOST}${hreflangPath}`);
      document.head.appendChild(xdef);
      alternateLinks.push(xdef);
    }

    // Per-page BreadcrumbList JSON-LD ------------------------------------------
    let breadcrumbScript = null;
    if (Array.isArray(crumbsData) && crumbsData.length) {
      // Remove any previously-injected breadcrumb script.
      document.head.querySelectorAll('script[data-dyn-breadcrumb="1"]').forEach((n) => n.remove());
      const payload = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: crumbsData.map((c, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: c.name,
          item: `${HOST}${c.url === '/' ? '/' : c.url}`,
        })),
      };
      breadcrumbScript = document.createElement('script');
      breadcrumbScript.type = 'application/ld+json';
      breadcrumbScript.setAttribute('data-dyn-breadcrumb', '1');
      breadcrumbScript.text = JSON.stringify(payload);
      document.head.appendChild(breadcrumbScript);
    }

    // Per-page FAQPage JSON-LD -------------------------------------------------
    let faqScript = null;
    if (Array.isArray(faqsData) && faqsData.length) {
      document.head.querySelectorAll('script[data-dyn-faq="1"]').forEach((n) => n.remove());
      const payload = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqsData.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      };
      faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.setAttribute('data-dyn-faq', '1');
      faqScript.text = JSON.stringify(payload);
      document.head.appendChild(faqScript);
    }

    // Per-page HowTo JSON-LD ---------------------------------------------------
    let howtoScript = null;
    if (howtoData) {
      const steps = Array.isArray(howtoData) ? howtoData : (Array.isArray(howtoData?.steps) ? howtoData.steps : null);
      if (steps && steps.length) {
        document.head.querySelectorAll('script[data-dyn-howto="1"]').forEach((n) => n.remove());
        const payload = {
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: (howtoData?.name) || title || 'How to',
          step: steps.map((s, i) => ({
            '@type': 'HowToStep',
            position: i + 1,
            name: s.name,
            text: s.text,
          })),
        };
        howtoScript = document.createElement('script');
        howtoScript.type = 'application/ld+json';
        howtoScript.setAttribute('data-dyn-howto', '1');
        howtoScript.text = JSON.stringify(payload);
        document.head.appendChild(howtoScript);
      }
    }

    return () => {
      // restore title + lang/dir for the next route
      document.title = prev.title;
      if (prev.lang) document.documentElement.setAttribute('lang', prev.lang);
      if (prev.dir)  document.documentElement.setAttribute('dir',  prev.dir);
      // clean up dynamic hreflangs so each route stamps its own fresh set
      alternateLinks.forEach((n) => n.remove());
      // remove breadcrumb JSON-LD
      if (breadcrumbScript && breadcrumbScript.parentNode) {
        breadcrumbScript.parentNode.removeChild(breadcrumbScript);
      }
      if (faqScript && faqScript.parentNode) {
        faqScript.parentNode.removeChild(faqScript);
      }
      if (howtoScript && howtoScript.parentNode) {
        howtoScript.parentNode.removeChild(howtoScript);
      }
    };
  }, [title, description, keywords, canonical, lang, dir, ogImage, ogType, hreflangPath, breadcrumbsKey, faqsKey, howtoKey]);
};

export default usePageMeta;
